import { useEffect, useLayoutEffect, useRef, type FormEvent } from 'react';
import type { Block as BlockT, TtsModel } from '@shared/types';
import { MAX_TAKES_PER_BLOCK } from '@shared/constants';
import { captureCursor, restoreCursor } from './restoreCursor';
import { TakeChip } from './TakeChip';
import { getParamSummary } from '@/lib/paramSummary';
import { voiceColorVar } from '@/lib/voiceMapping';
import { splitSentences } from '@/lib/splitSentences';
import { detectScript, scriptAttrs } from '@/lib/scriptDetect';
import { normalizeBlockText, normalizeDomHtml } from '@/lib/blockText';
import styles from './Block.module.css';

interface Props {
  idx: number;
  block: BlockT;
  totalBlocks: number;
  isActive: boolean;
  isNew: boolean;
  /** Neighbor lock state — disables this block's merge-up/merge-down buttons
   *  when the target neighbor is locked, since merging would overwrite the
   *  locked block's text. FB#19. */
  prevLocked?: boolean;
  nextLocked?: boolean;
  /** `true` while a TTS request is in-flight for this block. Disables text,
   *  controls, and Generate button; swaps Generate label to a spinner. */
  isGenerating: boolean;
  /** Index of the currently-playing take (from local play state in the
   *  parent). `-1` when no chip is playing. */
  playingTakeIdx: number;
  /** True when a Generate-All or Play-All-Favorites pipeline is currently
   *  working on this block (generating a take or playing a favorite). Adds
   *  the `.playing` yellow-arrow highlight. */
  isPipelineActive?: boolean;
  onActivate(idx: number): void;
  onTextChange(idx: number, text: string): void;
  onToggleLock(idx: number): void;
  onDuplicate(idx: number): void;
  onMergeUp(idx: number): void;
  onMergeDown(idx: number): void;
  onSplit(idx: number): void;
  onDelete(idx: number): void;
  /** Single-block MP3 download — favorited take of this block, named per
   *  the same convention the upload pipeline uses (langCode, shortId,
   *  asset title, version, voiceMarker for multi-voice). Disabled when
   *  no take is favorited yet. */
  onDownload(idx: number): void;
  onMoveUp(idx: number): void;
  onMoveDown(idx: number): void;
  onChipPlay(blockIdx: number, takeIdx: number): void;
  onChipFav(blockIdx: number, takeIdx: number): void;
  onChipDelete(blockIdx: number, takeIdx: number): void;
  /** Mobile (<600px) "Edit Params" affordance — opens the ParamPanel as a
   *  slide-up bottom sheet for this block. Hidden via CSS at desktop widths. */
  onOpenParams(idx: number): void;
  /** Receives the block element so the parent can position the param-panel
   *  arrow at this element's offsetTop when it becomes active. */
  registerRef(idx: number, el: HTMLDivElement | null): void;
  /** Multi-Voice (#173): list of allowed voiceIds on the active asset.
   *  Used to derive the per-take color via voiceColorVar. Pass `undefined`
   *  or an empty array for single-voice assets — TakeChip then falls back
   *  to the existing --brandcolor styling. */
  assetVoiceIds?: string[];
  /** Take id that should carry the once-per-device star hint (TTSView
   *  picks the very first generated take). */
  hintTakeId?: string;
  onHintDismiss?(): void;
}

/** Escape HTML for safe innerHTML interpolation. */
function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Wrap `[tag]` spans in v4 mode (audio tags) for purple inline highlight. */
function highlightTags(text: string, model: TtsModel): string {
  const safe = escHtml(text);
  if (model !== 'v4') return safe;
  return safe.replace(/(\[[^\]]*\])/g, '<span class="tag-hl">$1</span>');
}

/** Parse the counter out of a take id shaped `take-{N}-…`. Falls back to the
 *  given default if the id doesn't match (shouldn't happen in practice, but
 *  keeps old persisted data from crashing the render). */
function parseTakeNumber(id: string, fallback: number): number {
  const m = /^take-(\d+)/.exec(id);
  return m ? parseInt(m[1], 10) : fallback;
}

export function Block({
  idx,
  block,
  totalBlocks,
  isActive,
  isNew,
  prevLocked = false,
  nextLocked = false,
  isGenerating,
  playingTakeIdx,
  isPipelineActive = false,
  onActivate,
  onTextChange,
  onToggleLock,
  onDuplicate,
  onMergeUp,
  onMergeDown,
  onSplit,
  onDelete,
  onDownload,
  onMoveUp,
  onMoveDown,
  onChipPlay,
  onChipFav,
  onChipDelete,
  onOpenParams,
  registerRef,
  assetVoiceIds,
  hintTakeId,
  onHintDismiss,
}: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  /** Caret offset captured during onInput; restored after React re-renders
   *  the innerHTML (needed for v4 tag-hl spans that would otherwise steal
   *  the selection). Null means "no pending restore". */
  const pendingCursorRef = useRef<number | null>(null);
  /** True zwischen compositionstart und compositionend (IME: Chinesisch,
   *  Vietnamesisch, Thai …). Solange wird das DOM nicht angefasst und kein
   *  Zwischenstand nach oben gemeldet — ein innerHTML-Reset mitten in der
   *  Komposition zerstört die Eingabe (Audit 2026-09-24). */
  const composingRef = useRef(false);

  // Expose the root element up to the container for param-panel positioning.
  useEffect(() => {
    registerRef(idx, rootRef.current);
    return () => registerRef(idx, null);
  }, [idx, registerRef]);

  const model = block.params.model;
  const html = highlightTags(block.text, model);

  // Keep the contenteditable DOM text in sync with the block model. Re-sync
  // whenever the text or the model changes (model swap re-runs highlighting).
  // Der Abgleich ist NBSP-tolerant: Chrome serialisiert geschützte
  // Leerzeichen als `&nbsp;`, das Modell hält normale — ohne Toleranz wurde
  // das DOM bei jedem Input neu gesetzt.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    if (composingRef.current) return;
    if (normalizeDomHtml(el.innerHTML) !== html) {
      el.innerHTML = html;
      // Restore caret if we just propagated a user edit upstream.
      if (pendingCursorRef.current != null) {
        restoreCursor(el, pendingCursorRef.current);
        pendingCursorRef.current = null;
      }
    }
  }, [html]);

  const sentences = splitSentences(block.text);
  const canSplit = sentences.length > 1;
  // Thai hat keine Satzzeichen — Split wäre geraten, deshalb ganz ausgeblendet
  // (Spec 2026-09-23-script-support-design).
  const textScript = scriptAttrs(block.text);
  const splitAvailable = detectScript(block.text) !== 'thai';
  const canDelete = totalBlocks > 1;
  const canMoveUp = idx > 0;
  const canMoveDown = idx < totalBlocks - 1;

  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    // IME-Komposition läuft noch — der endgültige Text kommt mit
    // compositionend (das noch einmal hier landet).
    if (composingRef.current) return;
    const el = e.currentTarget;
    pendingCursorRef.current = captureCursor(el);
    // NFC + NBSP → Leerzeichen (lib/blockText.ts): Vietnamesisch in NFD
    // wurde sonst als Latein erkannt, NBSP brach den DOM-Abgleich.
    const text = normalizeBlockText(el.textContent ?? '');
    onTextChange(idx, text);
  };
  const handleCompositionStart = () => {
    composingRef.current = true;
  };
  const handleCompositionEnd = (e: FormEvent<HTMLDivElement>) => {
    composingRef.current = false;
    handleInput(e);
  };

  const rootCls = [
    styles.block,
    isActive && styles.active,
    isNew && styles.newBlock,
    block.locked && styles.locked,
    isPipelineActive && styles.playing,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={rootRef}
      className={rootCls}
      data-block-idx={idx}
      onClick={() => onActivate(idx)}
    >
      <div className={styles.playIndicator} aria-hidden />
      <div className={styles.num}>
        <button
          type="button"
          className={styles.ctrlBtn}
          onClick={(e) => {
            e.stopPropagation();
            onToggleLock(idx);
          }}
        >
          <i
            className={`${block.locked ? 'ph-fill ph-lock' : 'ph-bold ph-lock-open'} ${styles.lockIcon}`}
          />
          <span className={styles.tip}>{block.locked ? 'Unlock block' : 'Lock block'}</span>
        </button>
        <span className={styles.label}>Block {idx + 1}</span>
        <div className={styles.controls} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={styles.ctrlBtn}
            disabled={isGenerating}
            onClick={() => !isGenerating && onDuplicate(idx)}
          >
            <i className="ph-bold ph-copy" />
            <span className={styles.tip}>Duplicate block</span>
          </button>
          <button
            type="button"
            className={styles.ctrlBtn}
            disabled={isGenerating || block.favIdx < 0}
            onClick={() => block.favIdx >= 0 && !isGenerating && onDownload(idx)}
          >
            <i className="ph-bold ph-download-simple" />
            <span className={styles.tip}>
              {block.favIdx < 0
                ? 'Favorite a take first to download'
                : 'Download this block as MP3'}
            </span>
          </button>
          {/* All four control buttons render unconditionally so the row
              never reflows when canMoveUp/canDelete/etc. flip — that
              caused mid-edit layout shifts where a click landed on Delete
              by accident. Visual disable + tooltip explain why a button
              is greyed out. */}
          <button
            type="button"
            className={styles.ctrlBtn}
            disabled={!canMoveUp || block.locked || prevLocked || isGenerating}
            onClick={() =>
              !block.locked && !prevLocked && !isGenerating && onMergeUp(idx)
            }
          >
            <i className="ph-bold ph-arrows-merge" style={{ transform: 'rotate(180deg)' }} />
            <span className={styles.tip}>
              {!canMoveUp
                ? 'No block above'
                : prevLocked
                  ? 'Block above is locked'
                  : 'Merge with block above'}
            </span>
          </button>
          <button
            type="button"
            className={styles.ctrlBtn}
            disabled={!canMoveDown || block.locked || nextLocked || isGenerating}
            onClick={() =>
              !block.locked && !nextLocked && !isGenerating && onMergeDown(idx)
            }
          >
            <i className="ph-bold ph-arrows-merge" />
            <span className={styles.tip}>
              {!canMoveDown
                ? 'No block below'
                : nextLocked
                  ? 'Block below is locked'
                  : 'Merge with block below'}
            </span>
          </button>
          {splitAvailable && (
            <button
              type="button"
              className={styles.ctrlBtn}
              disabled={!canSplit || isGenerating}
              onClick={() => canSplit && !isGenerating && onSplit(idx)}
            >
              <i className="ph-bold ph-scissors" />
              <span className={styles.tip}>
                {canSplit ? 'Split block at sentence' : 'Nothing to split — single sentence'}
              </span>
            </button>
          )}
          <button
            type="button"
            className={styles.ctrlBtn}
            disabled={!canDelete || block.locked || isGenerating}
            onClick={() => canDelete && !block.locked && !isGenerating && onDelete(idx)}
          >
            <i className="ph-bold ph-x" />
            <span className={styles.tip}>
              {!canDelete ? 'Can’t delete the last block' : 'Delete block'}
            </span>
          </button>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.moveBtns} onClick={(e) => e.stopPropagation()}>
          {canMoveUp ? (
            <button type="button" className={styles.moveBtn} onClick={() => onMoveUp(idx)} title="Move up">
              <i className="ph-bold ph-caret-up" />
            </button>
          ) : (
            <div className={styles.moveSpacer} />
          )}
          {canMoveDown ? (
            <button type="button" className={styles.moveBtn} onClick={() => onMoveDown(idx)} title="Move down">
              <i className="ph-bold ph-caret-down" />
            </button>
          ) : (
            <div className={styles.moveSpacer} />
          )}
        </div>
        <div
          ref={textRef}
          className={styles.text}
          {...textScript}
          // Während der Generierung ist der Text gesperrt — der Take gehört
          // zum abgeschickten Text, und der Request soll nicht ins Leere
          // laufen (Audit M7).
          contentEditable={!block.locked && !isGenerating}
          suppressContentEditableWarning
          onInput={handleInput}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          onClick={(e) => {
            e.stopPropagation();
            onActivate(idx);
          }}
          onBlur={() => window.getSelection()?.removeAllRanges()}
        />
      </div>

      <div className={styles.chipsRow}>
        {block.takes.map((take, ti) => {
          // Take number is baked into the id at creation time (`take-{N}-…`)
          // so numbering stays stable when the oldest non-fav is evicted
          // (legacy parity: Take 1 stays Take 1 even after Take 2/3 eviction).
          const takeNumber = parseTakeNumber(take.id, ti + 1);
          // Multi-Voice (#173): per-take color from the take's voiceId.
          // Falsy when single-voice (TakeChip falls back to --brandcolor).
          const voiceColor =
            assetVoiceIds && assetVoiceIds.length >= 2 && take.voiceId
              ? voiceColorVar(take.voiceId, assetVoiceIds)
              : undefined;
          return (
            <TakeChip
              key={take.id}
              takeNumber={takeNumber}
              isFav={ti === block.favIdx}
              isNewest={ti === block.takes.length - 1}
              isPlaying={ti === playingTakeIdx}
              onPlayToggle={() => onChipPlay(idx, ti)}
              onFavToggle={() => onChipFav(idx, ti)}
              onDelete={() => onChipDelete(idx, ti)}
              voiceColor={voiceColor}
              hint={take.id === hintTakeId}
              onHintDismiss={onHintDismiss}
            />
          );
        })}
        {isGenerating && (
          <span className={styles.generatingHint}>Generating…</span>
        )}
      </div>

      <div className={styles.summary}>
        {block.favIdx >= 0 && block.takes[block.favIdx]
          ? getParamSummary(
              block.takes[block.favIdx].paramsSnapshot,
              block.takes[block.favIdx].duration,
            )
          : ''}
      </div>

      <button
        type="button"
        className={styles.editParamsBtn}
        onClick={(e) => {
          e.stopPropagation();
          onOpenParams(idx);
        }}
      >
        <i className="ph-bold ph-sliders" /> Edit Params
      </button>

      {/* Discreet take-count badge at the bottom-right corner — talent
          can see how close they are to the per-block 5-take cap without
          counting chips. */}
      <span className={styles.takeCount} aria-hidden="true">
        {block.takes.length} / {MAX_TAKES_PER_BLOCK}
      </span>
    </div>
  );
}
